/* global mapboxgl */
import React from 'react'
import { findDOMNode } from 'react-dom'
// mapbox styles are only 8k, so it seems ok to import them statically
import 'mapbox-gl/dist/mapbox-gl.css'
import styles from './MapboxMapEngine.scss'
import { Viewport } from 'data/records'
import { MapTypeContext } from 'common/Map/MapTypeContext'
import { getMapboxStyle } from './getMapboxStyle'
import { onMapboxReady } from 'lib/mapboxUtils'
import { RenderBigMenuItem } from 'common/GlobalNav/AdminMenu/Items'

// nasty semiglobals
let MAPBOX_LOAD_PENDING = false
let MAPBOX_LOAD_CALLBACKS = []

export class MapboxMapEngine extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      mapContext: null,
      initialViewport: props.initialViewport, // make copies so these can't be changed
      initialViewportPadding: props.initialViewportPadding
    }
  }

  componentDidMount () {
    if (this.initialized()) {
      this.initMap()
    } else if (MAPBOX_LOAD_PENDING) {
      MAPBOX_LOAD_CALLBACKS.push(this.initMap)
    } else {
      this.lazyLoadMapbox()
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevProps.overlay !== this.props.overlay) {
      this.updateMapStyle()
    }
    if (prevProps.showHeatLayer !== this.props.showHeatLayer) {
      this.toggleHeatLayer()
    }
    const { scrollWheelZoom } = this.props
    if (this._map && (prevProps.scrollWheelZoom !== scrollWheelZoom)) {
      scrollWheelZoom
        ? this._map.scrollZoom.enable()
        : this._map.scrollZoom.disable()
    }
  }

  initialized = () => window.mapboxgl

  initMap = () => {
    const { overlay, container, scrollWheelZoom, showHeatLayer } = this.props
    const { initialViewport, initialViewportPadding } = this.state
    const center = Viewport.getCenter(initialViewport)
    // default zoom of 10 will be immediately clobbered by the fitbounds in the
    // event that our initialViewport is a {bounds} instead of a {center, zoom}

    // mapbox-gl-js uses a tile size of 512px instead of 256px, so zoom must be
    // offset by 1 in order to match our other mapping render systems
    const zoom = (initialViewport.zoom || 10) - 1
    const style = getMapboxStyle(overlay)

    this._map = new mapboxgl.Map({
      center,
      zoom,
      container: container ?? findDOMNode(this),
      style: style,
      scrollZoom: scrollWheelZoom
    })

    this._map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')

    this._map.setMaxZoom(overlay.maxZoom)
    this._map.setMinZoom(overlay.minZoom)

    this._map.on('click', this.onMapClick)
    this._map.on('moveend', this.onMoveEnd)

    initialViewport.bounds && this._map.fitBounds(initialViewport.bounds, {
      padding: initialViewportPadding || 80,
      linear: true,
      duration: 0
    })

    this._map.on('load', () => {
      this.initializeHeatLayer()
      if (showHeatLayer) this.toggleHeatLayer()
    })

    this.setState({
      mapContext: {
        mapType: 'mapbox',
        mapInstance: this._map,
        mapEngine: this
      }
    })
  }

  updateMapStyle = () => {
    onMapboxReady(this._map, () => {
      this._map.removeLayer('raster-basemap')
      const layers = this._map.getStyle().layers
      const bottomLayer = layers[0]
      const { overlay } = this.props
      const newBasemap = {
        id: 'raster-basemap',
        type: 'raster',
        source: overlay.id,
        minzoom: overlay.minZoom,
        maxZoom: overlay.maxZoom
      }
      this._map.addLayer(newBasemap, bottomLayer.id)
      this._map.setMaxZoom(overlay.maxZoom)
      this._map.setMinZoom(overlay.minZoom)
    })
  }

  // our parent's height changed, so we need to reset the canvas height
  resizeToParentHeight = () => this._map.resize()

  // immediately set viewport, with no animation
  jumpTo = (viewport) => {
    if (viewport.bounds) {
      this._map.fitBounds(viewport.bounds, { padding: 80, linear: true, duration: 0 })
      return
    }
    // mapbox-gl-js uses a tile size of 512px instead of 256px, so zoom must be
    // offset by 1 in order to match our other mapping render systems
    if (viewport.zoom && viewport.center) {
      this._map.jumpTo({
        center: viewport.center,
        zoom: viewport.zoom - 1
      })
    }
  }

  // fly to a viewport, with animation
  flyTo = (viewport, options = {}) => {
    const padding = options.padding || { top: 80, right: 80, bottom: 80, left: 80 }
    if (viewport.bounds) {
      this._map.fitBounds(viewport.bounds, { padding })
      return
    }
    // mapbox-gl-js uses a tile size of 512px instead of 256px, so zoom must be
    // offset by 1 in order to match our other mapping render systems
    if (viewport.zoom && viewport.center) {
      this._map.flyTo({
        center: viewport.center,
        zoom: viewport.zoom - 1
      })
    }
  }

  latLngToPixelCoordinate = (latLng) => {
    // this gives us a nice {x, y} plain object from the top-left of the viewport
    return this._map.project(latLng)
  }

  lazyLoadMapbox = () => {
    MAPBOX_LOAD_PENDING = true
    console.log('Starting Mapbox CDN lazy load')
    const script = document.createElement('script')
    const src = 'https://api.tiles.mapbox.com/mapbox-gl-js/v1.3.1/mapbox-gl.js'
    window.onLazyLoadMapboxComplete = () => {
      // temp access token, we will want to pull this from sever-provided config
      mapboxgl.accessToken = 'pk.eyJ1IjoicXVpbm4tcndncHMiLCJhIjoiY2swZTU3NjVvMGVnNzNkcm43bDRtdjVnbSJ9.K8Awxcjp8C4zjELtIZtktA'
      console.log('Mapbox lazy load completed, running callbacks')
      // clean up
      delete window.onLazyLoadMapboxComplete
      MAPBOX_LOAD_PENDING = false
      MAPBOX_LOAD_CALLBACKS.forEach(cb => cb())
      MAPBOX_LOAD_CALLBACKS = []
      this.initMap()
    }
    script.setAttribute('src', src)
    script.setAttribute('onload', 'window.onLazyLoadMapboxComplete()')
    document.body.appendChild(script)
  }

  initializeHeatLayer = () => {
    this._map.addSource('heatmap', {
      type: 'raster',
      tiles: [
        '//heatmap.ridewithgps.com/normalized/{z}/{x}/{y}.png?start_color=%23bcebf0&end_color=%23e94b65'
      ],
      tileSize: 256
    })
    this._map.addLayer({
      id: 'heatmap',
      source: 'heatmap',
      type: 'raster',
      minZoom: 3,
      maxZoom: 15
    })
    this._map.setPaintProperty(
      'heatmap',
      'raster-opacity',
      0.7
    )
    this._map.setLayoutProperty(
      'heatmap',
      'visibility',
      'none'
    )
  }

  toggleHeatLayer = () => {
    const { showHeatLayer } = this.props
    this._map.setLayoutProperty(
      'heatmap',
      'visibility',
      showHeatLayer ? 'visible' : 'none'
    )
  }

  onMapClick = (e) => {
    if (this.props.onMapClick) {
      const feature = this._map.queryRenderedFeatures(e.point)[0]
      this.props.onMapClick({ e, feature })
    }
  }

  onMoveEnd = () => {
    if (this.props.onViewportChanged && this._map) {
      const _center = this._map.getCenter()
      const center = { lat: _center.lat, lng: _center.lng }
      const zoom = Math.round(this._map.getZoom() + 1)
      this.props.onViewportChanged(Viewport({ center, zoom }))
    }
  }

  render () {
    const { children } = this.props
    const { mapContext } = this.state
    return (
      <div className={styles.MapboxMapEngine}>
        <RenderBigMenuItem getEast={() => this._map.getBounds()._ne.lng}
          getWest={() => this._map.getBounds()._sw.lng}
          getNorth={() => this._map.getBounds()._ne.lat}
          getSouth={() => this._map.getBounds()._sw.lat} />
        <MapTypeContext.Provider value={mapContext}>
          {mapContext && children}
        </MapTypeContext.Provider>
      </div>
    )
  }
}
